import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { JsonInput } from '@shared/components/JsonInput';

import { VoicePresetSelect } from './VoicePresetSelect';
import { useVoiceSettingsStyles } from './styles';
import type {
  TextVoiceSettingKey,
  VoiceSettingChange,
  VoiceSettingKey,
  VoiceSettingValue,
  VoiceSettingsObject,
} from './types';
import { sonioxSampleRates, VOICE_PRESETS as presets } from './voiceSettings.presets';
import {
  numberRule,
  type NumericSettingKey,
  readSetting,
  validNumberInput,
} from './voiceSettings.utils';

/** Keys under superAdminManageVoice.advanced.hints. */
type HintKey =
  | 'instructions'
  | 'refAudio'
  | 'refText'
  | 'openaiInstructions'
  | 'instructionsPreset'
  | 'stability'
  | 'similarityBoost'
  | 'style'
  | 'streamingLatency'
  | 'speed'
  | 'expressiveness'
  | 'bitrate';

/** Settings measured in a unit; the unit goes into the field label. */
const UNITS: Partial<Record<VoiceSettingKey, string>> = {
  sampleRate: 'Hz',
  bitrate: 'bps',
};

interface Props {
  provider: string;
  settings: VoiceSettingsObject;
  rawJson: string;
  /** Message for the JSON field: syntax, root or disallowed credential keys. */
  jsonError?: string;
  /** Unparseable JSON locks dedicated controls; a parsed JSON keeps them editable. */
  jsonInvalid?: boolean;
  drafts: Partial<Record<NumericSettingKey, string>>;
  expanded: boolean;
  disabled?: boolean;
  onExpandedChange: (expanded: boolean) => void;
  onJsonChange: (text: string) => void;
  onJsonBlur: () => void;
  onSettingChange: VoiceSettingChange;
  onInvalidNumber: (key: NumericSettingKey, text: string) => void;
}

export const VoiceAdvancedSettings = ({
  provider,
  settings,
  rawJson,
  jsonError,
  jsonInvalid = false,
  drafts,
  expanded,
  disabled = false,
  onExpandedChange,
  onJsonChange,
  onJsonBlur,
  onSettingChange,
  onInvalidNumber,
}: Props) => {
  const { t } = useTranslation();
  const { classes, cx } = useVoiceSettingsStyles();
  const label = (key: VoiceSettingKey) => {
    const name = t(`superAdminManageVoice.advanced.fields.${key}`);
    return UNITS[key] ? `${name} (${UNITS[key]})` : name;
  };
  const hasErrors = Boolean(jsonError) || Object.keys(drafts).length > 0;
  const display = (value: string | number | boolean) =>
    typeof value === 'boolean' ? t(`superAdminManageVoice.advanced.${value}`) : String(value);
  const hint = (fallback?: string | number | boolean) =>
    fallback === undefined
      ? t('superAdminManageVoice.advanced.optional')
      : t('superAdminManageVoice.advanced.defaultHint', { value: display(fallback) });
  const jsonManaged = t('superAdminManageVoice.advanced.jsonManaged');

  const select = <K extends VoiceSettingKey>(
    key: K,
    options: readonly NonNullable<VoiceSettingValue<NoInfer<K>>>[],
    fallback?: string | number | boolean,
  ) => (
    <VoicePresetSelect
      key={key}
      id={`voice-setting-${key}`}
      label={label(key)}
      value={readSetting(settings, provider, key)}
      options={options}
      disabled={disabled || jsonInvalid}
      fallbackHint={hint(fallback)}
      onChange={(value) => onSettingChange(key, value)}
    />
  );

  const number = (key: NumericSettingKey, fallback?: number, hintKey?: HintKey) => {
    const value = readSetting(settings, provider, key);
    const draft = drafts[key];
    const rule = numberRule(provider, key);
    const inputProps = { min: rule.min, max: rule.max, step: rule.integer ? 1 : 'any' };
    const bounds =
      rule.min === undefined
        ? ''
        : rule.max === undefined
          ? ` ≥ ${rule.min}`
          : ` ${rule.min}–${rule.max}`;
    const constraint = t('superAdminManageVoice.advanced.numberHint', {
      kind: t(`superAdminManageVoice.advanced.${rule.integer ? 'integer' : 'number'}`),
      bounds,
    });
    const description = hintKey ? `${t(`superAdminManageVoice.advanced.hints.${hintKey}`)} ` : '';
    const represented = typeof value === 'number' && validNumberInput(provider, key, String(value));
    return (
      <TextField
        key={key}
        id={`voice-setting-${key}`}
        fullWidth
        margin="dense"
        label={label(key)}
        type="number"
        inputProps={inputProps}
        disabled={disabled || jsonInvalid}
        value={draft ?? (represented ? value : '')}
        error={draft !== undefined}
        helperText={
          draft !== undefined
            ? `${t('superAdminManageVoice.advanced.invalidNumber')} ${constraint}`
            : value !== undefined && !represented
              ? jsonManaged
              : `${description}${constraint} ${hint(fallback)}`
        }
        onChange={(event) => {
          const text = event.target.value;
          if (
            (event.target as HTMLInputElement).validity.badInput ||
            (text !== '' && !validNumberInput(provider, key, text))
          ) {
            onInvalidNumber(key, text);
          } else {
            onSettingChange(key, text === '' ? undefined : Number(text));
          }
        }}
      />
    );
  };

  const text = (key: TextVoiceSettingKey, multiline: boolean, hintKey: HintKey) => {
    const value = readSetting(settings, provider, key);
    return (
      <TextField
        key={key}
        id={`voice-setting-${key}`}
        fullWidth
        margin="dense"
        label={label(key)}
        multiline={multiline}
        minRows={multiline ? 3 : undefined}
        disabled={disabled || jsonInvalid}
        value={typeof value === 'string' ? value : ''}
        helperText={
          value !== undefined && typeof value !== 'string'
            ? jsonManaged
            : t(`superAdminManageVoice.advanced.hints.${hintKey}`)
        }
        onChange={(event) => onSettingChange(key, event.target.value || undefined)}
      />
    );
  };

  const controls = () => {
    switch (provider) {
      case '11labs':
        return [
          number('stability', 0.4, 'stability'),
          number('similarityBoost', 0.6, 'similarityBoost'),
          number('style', 0, 'style'),
          select('useSpeakerBoost', [true, false], true),
          number('streamingLatency', 2, 'streamingLatency'),
        ];
      case 'hamsa':
        return [
          select('speaker', presets.hamsa.speaker),
          select('dialect', presets.hamsa.dialect),
          select('mulaw', [true, false], false),
          number('speed', 1, 'speed'),
          number('expressiveness', 1, 'expressiveness'),
          select('sampleRate', presets.hamsa.sampleRate, 16000),
          select('normalizeAudio', [true, false], true),
        ];
      case 'deepgram':
        return [text('instructionsPreset', false, 'instructionsPreset')];
      case 'soniox':
        return [
          select('audioFormat', presets.soniox.audioFormat, 'pcm_s16le'),
          select(
            'sampleRate',
            sonioxSampleRates(readSetting(settings, provider, 'audioFormat')),
            24000,
          ),
          number('bitrate', undefined, 'bitrate'),
        ];
      // Grok and Azure have no wired advanced settings: JSON only.
      case 'grok-tts':
      case 'azure':
        return [];
      case 'qwen3-tts':
        return [
          // Grouped by height so each row lines up: selects, multiline texts, then the URL.
          select('taskType', presets['qwen3-tts'].taskType, 'CustomVoice'),
          select('responseFormat', presets['qwen3-tts'].responseFormat, 'pcm'),
          text('instructions', true, 'instructions'),
          text('refText', true, 'refText'),
          text('refAudio', false, 'refAudio'),
        ];
      case 'openai':
        return [
          number('speed', 1, 'speed'),
          select('responseFormat', presets.openai.responseFormat),
          text('instructions', true, 'openaiInstructions'),
        ];
      default:
        return [];
    }
  };

  return (
    <Accordion
      disableGutters
      elevation={0}
      expanded={expanded}
      onChange={(_, next) => onExpandedChange(next)}
      className={cx(classes.section, hasErrors && classes.sectionError)}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="voice-advanced-settings-content"
        id="voice-advanced-settings-header">
        <Box>
          <Typography variant="subtitle1">{t('superAdminManageVoice.advanced.title')}</Typography>
          {hasErrors && (
            <Typography variant="caption" color="error" role="alert">
              {t('superAdminManageVoice.advanced.fixErrors')}
            </Typography>
          )}
        </Box>
      </AccordionSummary>
      <AccordionDetails id="voice-advanced-settings-content">
        <Box className={classes.fieldsGrid}>{controls()}</Box>
        <JsonInput
          id="voice-settings-json"
          label={t('superAdminManageVoice.form.voiceSettings')}
          rows={4}
          value={rawJson}
          disabled={disabled}
          onChange={onJsonChange}
          onBlur={onJsonBlur}
          error={jsonError}
          sx={{ mt: 2 }}
        />
      </AccordionDetails>
    </Accordion>
  );
};
